import React, { useEffect, useRef, useState, useCallback } from "react";

export interface MultiSelectOption {
 value: string;
 label: string;
 disabled?: boolean;
}

export interface MultiSelectProps {
 label?: string;
 error?: string;
 helperText?: string;
 options: MultiSelectOption[];
 value: string[];
 onChange: (values: string[]) => void;
 placeholder?: string;
 className?: string;
 id?: string;
 disabled?: boolean;
 isFontSelector?: boolean; // If true, style each option with its font family
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
 label,
 error,
 helperText,
 options,
 value = [],
 onChange,
 placeholder = "Select options...",
 className = "",
 id,
 disabled = false,
 isFontSelector = false,
}) => {
 const [searchQuery, setSearchQuery] = useState("");
 const [isOpen, setIsOpen] = useState(false);
 const containerRef = useRef<HTMLDivElement>(null);
 const dropdownRef = useRef<HTMLDivElement>(null);
 const inputRef = useRef<HTMLInputElement>(null);
 const selectId = id || `multiselect-${Math.random().toString(36).substr(2, 9)}`;
 const hasError = !!error;

 // Helper function to format font family for CSS
 const formatFontFamily = (fontName: string): string => {
  if (!fontName) return "";
  if (fontName.includes(",")) return fontName; // Already formatted
  return fontName.includes(" ") ? `"${fontName}", sans-serif` : `${fontName}, sans-serif`;
 };

 // Load Google Font dynamically
 const loadFont = useCallback((fontName: string) => {
  if (!fontName || !isFontSelector) return;

  // System fonts don't need loading
  const systemFonts = [
   "Arial", "Helvetica", "Times New Roman", "Courier New", "Verdana",
   "Georgia", "Palatino", "Garamond", "Comic Sans MS", "Trebuchet MS",
   "Impact", "Lucida Console", "Tahoma"
  ];
  
  if (systemFonts.includes(fontName)) return;

  // Check if font is already loaded
  const linkId = `font-${fontName.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(linkId)) return;

  // Load Google Font
  const link = document.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  const encodedName = encodeURIComponent(fontName);
  
  // Check if it's an Arabic font
  const isArabicFont = fontName.toLowerCase().includes("arabic") || 
   ["Cairo", "Tajawal", "Amiri", "Almarai", "Changa", "El Messiri",
    "Harmattan", "Lateef", "Markazi Text", "Reem Kufi", "Scheherazade New",
    "Noto Sans Arabic", "Noto Naskh Arabic", "Noto Kufi Arabic",
    "IBM Plex Sans Arabic", "Vibes"].includes(fontName);
  const subset = isArabicFont ? "&subset=arabic" : "";
  
  link.href = `https://fonts.googleapis.com/css2?family=${encodedName}:wght@400;700${subset}&display=swap`;
  document.head.appendChild(link);
 }, [isFontSelector]);

 // Filter options based on search query and exclude already selected
 const filteredOptions = options.filter((option) => {
  const matchesSearch = option.label.toLowerCase().includes(searchQuery.toLowerCase());
  const notSelected = !value.includes(option.value);
  return matchesSearch && notSelected && !option.disabled;
 });

 // Load fonts when dropdown opens
 useEffect(() => {
  if (isOpen && isFontSelector) {
   filteredOptions.forEach(option => {
    loadFont(option.value);
   });
  }
 }, [isOpen, filteredOptions, loadFont, isFontSelector]);

 // Handle click outside to close dropdown
 useEffect(() => {
  if (!isOpen) return;

  const handleClickOutside = (event: MouseEvent | TouchEvent) => {
   const target = event.target as Node;
   
   // Check if click is outside the container (which contains both input and dropdown)
   if (containerRef.current && !containerRef.current.contains(target)) {
    setIsOpen(false);
    setSearchQuery("");
   }
  };

  // Use both click and touchstart events to handle all cases
  // Use capture phase to catch events before they're stopped by Modal
  document.addEventListener("click", handleClickOutside, true);
  document.addEventListener("touchend", handleClickOutside, true);

  return () => {
   document.removeEventListener("click", handleClickOutside, true);
   document.removeEventListener("touchend", handleClickOutside, true);
  };
 }, [isOpen]);

 const handleSelect = (optionValue: string) => {
  if (!value.includes(optionValue)) {
   onChange([...value, optionValue]);
  }
  setSearchQuery("");
  setIsOpen(false);
  inputRef.current?.focus();
 };

 const handleRemove = (optionValue: string) => {
  onChange(value.filter((v) => v !== optionValue));
 };

 const getSelectedLabels = () => {
  return value.map((val) => {
   // Normalize both values to strings for comparison
   const normalizedVal = String(val);
   const option = options.find((opt) => String(opt.value) === normalizedVal);
   // Return label if found, otherwise return the value itself (for display while loading)
   return option?.label || normalizedVal;
  });
 };

 return (
  <div className={`w-full ${className}`}>
   {label && (
    <label
     htmlFor={selectId}
     className="block text-sm font-medium text-gray-700 mb-1"
    >
     {label}
    </label>
   )}
   <div 
    className="relative" 
    ref={containerRef}
    onClick={() => {
     // Don't stop propagation - the click-outside handler checks if target is inside container
     if (!disabled) {
      inputRef.current?.focus();
     }
    }}
   >
    <div
     className={`
      w-full min-h-[42px] px-3 py-2 border  bg-white
      focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500
      ${hasError ? "border-red-500 focus-within:ring-red-500 focus-within:border-red-500" : "border-gray-300"}
      ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}
      flex flex-wrap gap-2 items-center
     `}
    >
     {/* Selected items as chips */}
     {value.length > 0 && (
      <>
       {getSelectedLabels().map((label, index) => {
        const optionValue = value[index];
        const fontFamily = isFontSelector ? formatFontFamily(optionValue) : undefined;
        
        // Load font for selected chip
        if (isFontSelector && optionValue) {
         loadFont(optionValue);
        }
        
        return (
         <span
          key={optionValue}
          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm font-medium"
         >
          <span style={fontFamily ? { fontFamily } : undefined}>{label}</span>
          {!disabled && (
           <button
            type="button"
            onClick={(e) => {
             e.stopPropagation();
             handleRemove(optionValue);
            }}
            className="hover:bg-blue-200 p-0.5 transition-colors"
            aria-label={`Remove ${label}`}
           >
            <svg
             className="w-3 h-3"
             fill="none"
             stroke="currentColor"
             viewBox="0 0 24 24"
            >
             <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
             />
            </svg>
           </button>
          )}
         </span>
        );
       })}
      </>
     )}

     {/* Search input */}
     <input
      ref={inputRef}
      id={selectId}
      type="text"
      value={searchQuery}
      onChange={(e) => {
       setSearchQuery(e.target.value);
       setIsOpen(true);
      }}
      onFocus={() => setIsOpen(true)}
      placeholder={value.length === 0 ? placeholder : ""}
      disabled={disabled}
      className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
      aria-invalid={hasError}
      aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
     />
    </div>

    {/* Dropdown */}
    {isOpen && !disabled && (
     <div
      ref={dropdownRef}
      className="absolute z-50 w-full mt-1 bg-white border border-gray-300  max-h-60 overflow-y-auto"
     >
      {filteredOptions.length > 0 ? (
       <ul className="py-1">
        {filteredOptions.map((option) => {
         const fontFamily = isFontSelector ? formatFontFamily(option.value) : undefined;
         
         // Load font when rendering option
         if (isFontSelector) {
          loadFont(option.value);
         }
         
         return (
          <li
           key={option.value}
           onClick={() => handleSelect(option.value)}
           className="px-4 py-2 cursor-pointer text-sm text-gray-700 hover:bg-blue-50 transition-colors flex items-center gap-2"
          >
           {isFontSelector && (
            <span className="text-lg flex-shrink-0" style={{ fontFamily }}>
             Aa أبج
            </span>
           )}
           <span style={fontFamily ? { fontFamily } : undefined}>{option.label}</span>
          </li>
         );
        })}
       </ul>
      ) : (
       <div className="px-4 py-3 text-sm text-gray-500">
        {searchQuery ? "No results found" : "All options selected"}
       </div>
      )}
     </div>
    )}
   </div>

   {error && (
    <p id={`${selectId}-error`} className="mt-1 text-sm text-red-600" role="alert">
     {error}
    </p>
   )}
   {helperText && !error && (
    <p id={`${selectId}-helper`} className="mt-1 text-sm text-gray-500">
     {helperText}
    </p>
   )}
  </div>
 );
};

export default MultiSelect;

